import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../services/auth.service';
import { default as ReactSelect } from "react-select";
import axios from 'axios';
import styled from '@emotion/styled';
import { isMobile } from 'react-device-detect';
import { conversationDebug } from '../utils/conversationDebug';
import {
    Box, 
    Typography, 
    Avatar, 
    TextField, 
    Button, 
    Chip, 
    Divider, 
    IconButton,
    Paper,
    LinearProgress,
    Tooltip,
    Fade,
    Slide,
    Zoom
} from '@mui/material';
import {
    Edit as EditIcon,
    WhatsApp as WhatsAppIcon,
    Send as SendIcon,
    AccessTime as TimeIcon,
    Label as LabelIcon,
    Person as PersonIcon,
    Description as TemplateIcon,
    Phone as PhoneIcon,
    Save as SaveIcon
} from '@mui/icons-material';

// Enhanced styled components with better aesthetics
const SidebarContainer = styled(Box)(({ theme }) => ({
    width: '20%',
    height: '100%',
    background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
    borderLeft: '1px solid #e9edef',
    overflowY: 'auto',
    padding: '32px 24px',
    '&::-webkit-scrollbar': {
        width: '6px',
    },
    '&::-webkit-scrollbar-track': {
        background: '#f1f1f1',
        borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb': {
        background: '#c1c1c1',
        borderRadius: '3px',
        '&:hover': {
            background: '#a8a8a8',
        },
    },
}));

const ContactHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    padding: '20px',
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
    borderRadius: '16px',
    marginBottom: '32px',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
    transition: 'all 0.3s ease',
    minHeight: '100px',
    '&:hover': {
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        transform: 'translateY(-2px)',
    },
}));

const ContactInfo = styled(Box)(({ theme }) => ({
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    width: '100%',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontSize: '13px',
    fontWeight: 700,
    color: '#008069',
    marginBottom: '12px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    '&::after': {
        content: '""',
        flex: 1,
        height: '1px',
        background: 'linear-gradient(90deg, #008069 0%, transparent 100%)',
        marginLeft: '12px',
    },
}));

const SectionContainer = styled(Box)(({ theme }) => ({
    marginBottom: '28px',
    padding: '20px',
    background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
    borderRadius: '16px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.02)',
    transition: 'all 0.3s ease',
    '&:hover': {
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
    },
}));

const TimerContainer = styled(Box)(({ theme }) => ({
    padding: '16px',
    background: 'linear-gradient(135deg, #fff8e1 0%, #fff3cd 100%)',
    border: '1px solid #ffd54f',
    borderRadius: '12px',
    marginBottom: '12px',
    boxShadow: '0 2px 8px rgba(255, 193, 7, 0.1)',
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(255, 193, 7, 0.15)',
    },
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
    width: 72,
    height: 72,
    background: 'linear-gradient(135deg, #008069 0%, #006d5a 100%)',
    color: '#ffffff',
    cursor: 'pointer',
    border: '3px solid #ffffff',
    boxShadow: '0 4px 12px rgba(0, 128, 105, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'scale(1.05) rotate(5deg)',
        boxShadow: '0 6px 20px rgba(0, 128, 105, 0.4)',
    },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiInput-root': {
        fontSize: '22px',
        fontWeight: 700,
        color: '#111b21',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
    },
    '& .MuiInput-root:before': {
        borderBottom: 'none',
    },
    '& .MuiInput-root:after': {
        borderBottom: 'none',
    },
    '& .MuiInput-root:hover:not(.Mui-disabled):before': {
        borderBottom: 'none',
    },
    '&:hover .MuiInput-root': {
        backgroundColor: '#f0f8f5',
        borderRadius: '8px',
        padding: '8px 12px',
        margin: '-8px -12px',
        border: '1px solid #008069',
    },
    '& .MuiInput-root.Mui-focused': {
        backgroundColor: '#f0f8f5',
        borderRadius: '8px',
        padding: '8px 12px',
        margin: '-8px -12px',
        border: '2px solid #008069',
    },
}));

const StyledNotesTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        transition: 'all 0.3s ease',
        '&:hover': {
            backgroundColor: '#fafbfc',
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#008069',
                borderWidth: '2px',
            },
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#008069',
            borderWidth: '2px',
        },
    },
    '& .MuiOutlinedInput-input': {
        fontSize: '14px',
        lineHeight: '1.6',
    },
}));

const StyledButton = styled(Button)(({ theme }) => ({
    background: 'linear-gradient(135deg, #008069 0%, #006d5a 100%)',
    borderRadius: '12px',
    padding: '12px 24px',
    fontWeight: 600,
    textTransform: 'none',
    fontSize: '14px',
    boxShadow: '0 4px 12px rgba(0, 128, 105, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
        background: 'linear-gradient(135deg, #006d5a 0%, #005a4a 100%)',
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(0, 128, 105, 0.4)',
    },
    '&:active': {
        transform: 'translateY(0)',
    },
}));

const PhoneDisplay = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: '#f0f8f5',
    borderRadius: '8px',
    border: '1px solid #e8f5e8',
    transition: 'all 0.3s ease',
    width: '100%',
    minWidth: 0,
    '&:hover': {
        backgroundColor: '#e8f5e8',
        borderColor: '#008069',
    },
}));

const ReactSelectElement = styled(ReactSelect)`
    .react-select__control {
        border: 2px solid #e9edef;
        border-radius: 12px;
        font-size: 14px;
        font-weight: 500;
        min-height: 44px;
        background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
        transition: all 0.3s ease;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
        &:hover {
            border-color: #008069;
            box-shadow: 0 4px 12px rgba(0, 128, 105, 0.1);
        }
        &:focus {
            border-color: #008069;
            box-shadow: 0 0 0 3px rgba(0, 128, 105, 0.15);
        }
    }
    .react-select__value-container {
        padding: 10px 16px;
    }
    .react-select__placeholder {
        color: #667781;
        font-weight: 400;
    }
    .react-select__single-value {
        color: #111b21;
        font-weight: 500;
    }
    .react-select__indicator-separator {
        display: none;
    }
    .react-select__indicator {
        color: #667781;
        transition: all 0.3s ease;
        &:hover {
            color: #008069;
            transform: scale(1.1);
        }
    }
    .react-select__menu {
        font-size: 14px;
        background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
        border: 1px solid #e9edef;
        border-radius: 12px;
        margin-top: 8px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
        overflow: hidden;
    }
    .react-select__menu-list {
        padding: 8px;
    }
    .react-select__option {
        padding: 12px 16px;
        border-radius: 8px;
        margin: 2px 0;
        transition: all 0.2s ease;
        &:hover {
            background: linear-gradient(135deg, #f0f8f5 0%, #e8f5e8 100%);
            transform: translateX(4px);
        }
    }
    .react-select__option--is-selected {
        background: linear-gradient(135deg, #008069 0%, #006d5a 100%);
        color: #ffffff;
        font-weight: 600;
    }
    .react-select__option--is-focused {
        background: linear-gradient(135deg, #f0f8f5 0%, #e8f5e8 100%);
    }
    .react-select__multi-value {
        background: linear-gradient(135deg, #e8f5e8 0%, #d4edda 100%);
        border-radius: 8px;
        color: #008069;
        padding: 4px 8px;
        margin: 2px;
        border: 1px solid #c3e6cb;
        font-weight: 500;
    }
    .react-select__multi-value__label {
        font-size: 12px;
        font-weight: 600;
    }
    .react-select__multi-value__remove {
        color: #008069;
        transition: all 0.2s ease;
        &:hover {
            color: #006d5a;
            transform: scale(1.1);
        }
    }
`;

// Helper function to check if a conversation session is still active (within 24 hours)
const isConversationActive = (conversation) => {
    if (!conversation || !conversation.expiration_timestamp) {
        return false;
    }
    
    const expirationTime = new Date(conversation.expiration_timestamp * 1000);
    const now = new Date();
    const timeLeft = expirationTime - now;
    
    return timeLeft > 0;
};

// Helper function to check if a message is a template message
const isTemplateMessage = (message) => {
    if (!message) return false;
    
    // Check if it's a template message (has text.body.name)
    if (message.text && 
        message.text.body && 
        typeof message.text.body === 'object' && 
        message.text.body.name) {
        return true;
    }
    
    return false;
};

// Helper function to check if last message allows 24-hour window
const canSendBasedOnLastMessage = (contact) => {
    if (!contact || !contact.lastMessage) {
        return false;
    }
    
    let lastMessage = contact.lastMessage;
    let lastMessageTimestamp = contact.lastMessageTimestamp;
    
    // If lastMessage is an object with entry structure, extract the actual message
    if (lastMessage.entry && 
        lastMessage.entry[0] && 
        lastMessage.entry[0].changes && 
        lastMessage.entry[0].changes[0] && 
        lastMessage.entry[0].changes[0].value && 
        lastMessage.entry[0].changes[0].value.messages && 
        lastMessage.entry[0].changes[0].value.messages[0]) {
        
        const messageData = lastMessage.entry[0].changes[0].value.messages[0];
        lastMessage = messageData;
        lastMessageTimestamp = messageData.timestamp;
    }
    
    // Check if the last message is a template message or from the contact
    const isTemplate = isTemplateMessage(lastMessage);
    const isFromContact = lastMessage.from !== process.env.REACT_APP_PHONE_NUMBER;
    
    // If it's a template message or from contact, check 24-hour window
    if (isTemplate || isFromContact) {
        const messageTime = new Date(lastMessageTimestamp * 1000);
        const now = new Date();
        const timeElapsed = now - messageTime;
        const twentyFourHours = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
        
        console.log('Last message 24h check:', {
            isTemplate,
            isFromContact,
            messageTime: messageTime.toLocaleString(),
            currentTime: now.toLocaleString(),
            timeElapsedHours: Math.floor(timeElapsed / (1000 * 60 * 60)),
            withinWindow: timeElapsed < twentyFourHours
        });
        
        return timeElapsed < twentyFourHours;
    }
    
    return false;
};

// Helper function to get active conversations from a contact
const getActiveConversations = (contact) => {
    if (!contact || !contact.conversation || !Array.isArray(contact.conversation)) {
        return [];
    }
    
    return contact.conversation.filter(isConversationActive);
};

export default function SidebarInfo(props) {
    const { t } = useTranslation();
    const [name, setName] = useState(props.contact?.NombreCompleto ? props.contact?.NombreCompleto : props.contact.Celular);
    const [number, setNumber] = useState(props.contact.Celular);
    const [notas, setNotas] = useState(props.contact?.notas ? props.contact?.notas : "");
    const [etiquetas, setEtiquetas] = useState([]);
    const [selectedTags, setSelectedTags] = useState(props.contact?.etiquetas ? (props.contact?.etiquetas) : []);
    const [updating, setUpdating] = useState(false);
    const [ejecutivoAsignado, setEjecutivoAsignado] = useState(props.contact?.ejecutivoAsignado ? (props.contact?.ejecutivoAsignado) : []);
    const [sucursalChat, setSucursalChat] = useState(props.contact?.sucursalChat ? (props.contact?.sucursalChat) : []);
    const [cbp, setCBP] = useState(props.contact?.conversation ? (props.contact?.conversation) : []);
    const [templates, setTemplates] = useState([]);
    const [plantillaSeleccionada, setPlantillaSeleccionada] = useState([]);
    const { user, logout } = useAuth();
    const [ejecutivos, setEjecutivos] = useState([]);
    const [expirationTimer, setExpirationTimer] = useState([]);
    const [expirationWidth, setExpirationWidth] = useState([]);
    const [expirationType, setExpirationType] = useState([]);
    const [timer, setTimer] = useState(undefined);

    useEffect(() => {
        setName(props.contact?.NombreCompleto ? props.contact?.NombreCompleto : props.contact.Celular);
        setNumber(props.contact.Celular);
        setNotas(props.contact?.notas ? props.contact?.notas : "");
        setSelectedTags(props.contact?.etiquetas ? (props.contact?.etiquetas) : []);
        setEjecutivoAsignado(props.contact?.ejecutivoAsignado ? (props.contact?.ejecutivoAsignado) : []);
        setSucursalChat(props.contact?.sucursalChat ? (props.contact?.sucursalChat) : []);
        setCBP(props.contact?.conversation ? (props.contact?.conversation) : []);

        async function fetchEtiquetas() {
            try {
                let url = `${process.env.REACT_APP_BACKEND_API}/tag`;
                let config = {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        "x-access-token": user.accessToken,
                    },
                };

                const response = await axios.get(url, config);
                let newEtiquetas = response.data.map((etiqueta) => {
                    return { value: etiqueta._id, label: etiqueta.name, selected: false };
                }
                );
                setEtiquetas(newEtiquetas);

            } catch (error) {
                console.log(error);
            }
        }
        fetchEtiquetas();
        async function fetchTemplates() {
            try {
                let url = `${process.env.REACT_APP_BACKEND_API}/template`;
                let config = {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        "x-access-token": user.accessToken,
                    },
                };

                const response = await axios.get(url, config);
                let newTemplates = response.data.map((template) => {
                    let isImageOrVideo = template['image-video-select'] ? template['image-video-select'].value : null;
                    if (isImageOrVideo == "image") {
                        return { value: template._id, label: template.name, image: template.image, video: "" };
                    } else if (isImageOrVideo == "video") {
                        return { value: template._id, label: template.name, video: template.video, image: "" };
                    } else {
                        return { value: template._id, label: template.name, video: template.video, image: template.image };
                    }
                }
                );
                setTemplates(newTemplates);

            } catch (error) {
                console.log(error);
            }
        }
        fetchTemplates();
    }, [props.contact.Celular]);

    useEffect(() => {
        if (updating)
            updateContactInfo();
    }, [updating]);

    useEffect(() => {
        // Initial check for active conversations and last message
        const activeConversations = getActiveConversations(props.contact);
        const analysis = conversationDebug.analyzeContact(props.contact);
        const canSendBasedOnMessage = canSendBasedOnLastMessage(props.contact);
        const canSendMessages = analysis.canSendMessages || canSendBasedOnMessage;
        
        console.log('=== Initial Timer Analysis ===');
        console.log('Contact:', props.contact.Celular);
        console.log('Conversation analysis:', analysis);
        console.log('Can send based on last message:', canSendBasedOnMessage);
        console.log('Final should enable input:', canSendMessages);
        
        if (props.onTimerStatusChange) {
            props.onTimerStatusChange(canSendMessages);
        }
        
        if (cbp.length > 0) {
            setExpirationTimer([]);
            setExpirationWidth([]);
            setExpirationType([]);
            if (timer)
                clearInterval(timer);
            setTimer(
                setInterval(() => {
                    expirationFunction();
                }, 10000)
            );
            const expirationFunction = () => {
                let tempExpirationTimer = [];
                let tempExpirationWidth = [];
                let tempExpirationType = [];
                let expiredTimers = 0;
                let activeConversations = 0;

                console.log('Checking conversation timers for contact:', props.contact.Celular);
                console.log('Total conversations:', props.contact.conversation.length);

                props.contact.conversation.forEach((conversation, idx) => {
                    let expirationTime = new Date(conversation.expiration_timestamp * 1000);
                    let now = new Date();
                    let timeLeft = expirationTime - now;
                    
                    console.log(`Conversation ${idx}:`, {
                        id: conversation.id,
                        type: conversation.origin.type,
                        expirationTimestamp: conversation.expiration_timestamp,
                        expirationTime: expirationTime.toLocaleString(),
                        currentTime: now.toLocaleString(),
                        timeLeftMs: timeLeft,
                        timeLeftHours: Math.floor(timeLeft / (1000 * 60 * 60)),
                        isActive: timeLeft > 0
                    });
                    
                    let skip = false;
                    if (timeLeft <= 0) {
                        // Session has expired (more than 24 hours old)
                        console.log(`Conversation ${idx} has expired`);
                        expiredTimers++;
                        skip = true;
                    }
                    
                    if (!skip) {
                        // Session is still active (within 24 hours)
                        activeConversations++;
                        let hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                        let minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));

                        tempExpirationTimer.push(hours + "h " + minutes + "m");
                        tempExpirationWidth.push(Math.max(0, Math.min(100, parseInt((timeLeft / (24 * 60 * 60 * 1000)) * 100))) + "%");
                        tempExpirationType.push(conversation.origin.type);
                    }
                });
                
                setExpirationTimer(tempExpirationTimer.map((expirationTimer) => expirationTimer));
                setExpirationWidth(tempExpirationWidth.map((expirationWidth) => expirationWidth));
                setExpirationType(tempExpirationType.map((expirationType) => expirationType));

                // Check both conversation timers and last message 24-hour window
                const hasActiveConversationTimer = activeConversations > 0;
                const canSendBasedOnMessage = canSendBasedOnLastMessage(props.contact);
                const hasActiveTimer = hasActiveConversationTimer || canSendBasedOnMessage;
                
                console.log(`Contact ${props.contact.Celular} timer status:`, {
                    activeConversations,
                    hasActiveConversationTimer,
                    canSendBasedOnMessage,
                    finalHasActiveTimer: hasActiveTimer
                });
                
                if (props.onTimerStatusChange) {
                    props.onTimerStatusChange(hasActiveTimer);
                }

                // Clear interval if all conversations have expired AND no 24h window from last message
                if (props.contact.conversation.length > 0 && activeConversations === 0 && !canSendBasedOnMessage) {
                    console.log('All conversations expired and no 24h window from last message, clearing timer');
                    if (timer) {
                        clearInterval(timer);
                    }
                }
            }
            expirationFunction();

        } else {
            setExpirationTimer([]);
            setExpirationWidth([]);
            setExpirationType([]);
            if (timer)
                clearInterval(timer);
        }
    }, [cbp]);

    useEffect(() => {
        async function fetchEjecutivos() {
            const url = `${process.env.REACT_APP_BACKEND_API}/ejecutivo`;
            const config = {
                headers: {
                    'x-access-token': user.accessToken,
                    'Content-Type': 'application/json',
                }
            }
            const response = await axios.get(url, config)
                .then((data) => {
                    let newEjecutivos = data.data.map((ejecutivo) => {
                        return { value: ejecutivo._id, label: ejecutivo.firstName + " " + ejecutivo.lastName, selected: ejecutivoAsignado.includes(ejecutivo._id) };
                    });
                    setEjecutivos(newEjecutivos);
                })
                .catch((error) => {
                    console.log(error);
                });
        }
        fetchEjecutivos();
    }, [sucursalChat]);

    function updateContactInfo() {
        setUpdating(false);
        fetch(`${process.env.REACT_APP_BACKEND_API}/contacts/${number}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                "x-access-token": user.accessToken,
            },
            body: JSON.stringify({
                wa_id: number,
                NombreCompleto: name,
                notas: notas,
                etiquetas: selectedTags,
                ejecutivoAsignado: ejecutivoAsignado,
                sucursalChat: sucursalChat,
            }),
        })
            .then((response) => response.json())
            .then((data) => {
                console.log('Success:', data);
                props.onUpdateContact(number, { NombreCompleto: name, notas: notas, etiquetas: selectedTags, ejecutivoAsignado: ejecutivoAsignado, sucursalChat: sucursalChat }, props.contact.inboxtype);
            }
            );
    }

        return (
        <Slide direction="left" in={true} timeout={400}>
            <SidebarContainer>
                {!number ? (
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        height: '100%',
                        textAlign: 'center',
                        padding: '40px 20px'
                    }}>
                        <Box sx={{ 
                            width: 80, 
                            height: 80, 
                            borderRadius: '50%', 
                            background: 'linear-gradient(135deg, #f0f8f5 0%, #e8f5e8 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '24px',
                            boxShadow: '0 4px 12px rgba(0, 128, 105, 0.1)'
                        }}>
                            <PersonIcon sx={{ fontSize: 40, color: '#008069' }} />
                        </Box>
                        <Typography variant="h6" sx={{ 
                            color: '#111b21', 
                            fontWeight: 600, 
                            marginBottom: '8px' 
                        }}>
                            {t('sidebar.noContactSelected')}
                        </Typography>
                        <Typography variant="body2" sx={{ 
                            color: '#667781', 
                            lineHeight: 1.5,
                            maxWidth: '200px'
                        }}>
                            {t('sidebar.selectContactMessage')}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <ContactHeader>
                            <Tooltip title={t('sidebar.clickToChangeProfile')} arrow>
                                <StyledAvatar
                                    src={process.env.REACT_APP_APP_LOGO || "https://firebasestorage.googleapis.com/v0/b/segenesis-cotizador.appspot.com/o/notificaciones%2FGenesisLOGO%20vida%20y%20salud%20(1).png?alt=media&token=2e7ce64b-a316-4579-b3de-37468e2fa470"} 
                                />
                            </Tooltip>
                            <ContactInfo>
                                <Tooltip title={t('sidebar.clickToEditName')} arrow>
                                    <StyledTextField
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                setUpdating(true);
                                                e.target.blur();
                                            }
                                        }}
                                        sx={{
                                            '& .MuiInput-root': {
                                                fontSize: '20px',
                                                fontWeight: 600,
                                                color: '#111b21',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            },
                                        }}
                                    />
                                </Tooltip>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <IconButton
                                        size="small"
                                        href={`https://wa.me/${number}`}
                                        target="_blank"
                                        sx={{ 
                                            color: '#008069',
                                            flexShrink: 0,
                                            '&:hover': { backgroundColor: '#e8f5e8' }
                                        }}
                                    >
                                        <WhatsAppIcon fontSize="small" />
                                    </IconButton>
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            color: '#667781',
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            wordBreak: 'break-all',
                                            flex: 1
                                        }}
                                    >
                                        {number}
                                    </Typography>
                                </Box>
                            </ContactInfo>
                        </ContactHeader>

            <SectionContainer>
                                    <SectionTitle>
                        <EditIcon sx={{ fontSize: 16, color: '#008069' }} />
                        {t('sidebar.notes')}
                    </SectionTitle>
                    <StyledNotesTextField
                        multiline
                        rows={4}
                        value={notas}
                        onChange={(e) => setNotas(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                setUpdating(true);
                                e.target.blur();
                            }
                        }}
                        placeholder={t('sidebar.notesPlaceholder')}
                />
            </SectionContainer>

            <Fade in={true} timeout={600}>
                <SectionContainer>
                    <SectionTitle>
                        <LabelIcon sx={{ fontSize: 16, color: '#008069' }} />
                        {t('sidebar.tags')}
                    </SectionTitle>
                    <ReactSelectElement
                        options={etiquetas}
                        value={selectedTags}
                        isMulti
                        onChange={(e) => {
                            setSelectedTags(e);
                            setUpdating(true);
                        }}
                        placeholder={t('sidebar.tagsPlaceholder')}
                    />
                </SectionContainer>
            </Fade>

            <Fade in={true} timeout={800}>
                <SectionContainer>
                    <SectionTitle>
                        <PersonIcon sx={{ fontSize: 16, color: '#008069' }} />
                        {t('sidebar.assignedExecutive')}
                    </SectionTitle>
                    <ReactSelectElement
                        options={ejecutivos}
                        value={ejecutivoAsignado}
                        isMulti
                        onChange={(e) => {
                            setEjecutivoAsignado(e);
                            setUpdating(true);
                        }}
                        placeholder={t('sidebar.assignedExecutivePlaceholder')}
                    />
                </SectionContainer>
            </Fade>

            <Fade in={true} timeout={1000}>
                <SectionContainer>
                    <SectionTitle>
                        <TemplateIcon sx={{ fontSize: 16, color: '#008069' }} />
                        {t('sidebar.sendTemplate')}
                    </SectionTitle>
                    <ReactSelectElement
                        options={templates}
                        value={plantillaSeleccionada}
                        onChange={(e) => {
                            setPlantillaSeleccionada(e);
                            setUpdating(true);
                        }}
                        placeholder={t('sidebar.templatePlaceholder')}
                    />
                    {plantillaSeleccionada && plantillaSeleccionada.value && (
                        <StyledButton
                            variant="contained"
                            startIcon={<SendIcon />}
                            onClick={(e) => props.onSelectTemplate(e, { 
                                name: plantillaSeleccionada.label, 
                                id: plantillaSeleccionada.value, 
                                image: plantillaSeleccionada.image, 
                                video: plantillaSeleccionada.video 
                            })}
                            fullWidth
                        >
                            {t('sidebar.sendTemplateButton')}
                        </StyledButton>
                    )}
                </SectionContainer>
            </Fade>


            {cbp && expirationTimer.map((expTimer, idx) => (
                <Zoom in={true} timeout={1200 + (idx * 200)}>
                    <TimerContainer key={idx}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <TimeIcon sx={{ fontSize: 18, color: '#856404' }} />
                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#856404', fontSize: '13px' }}>
                            {expirationType[idx]} {t('sidebar.session')}
                        </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: '#856404', mb: 2, fontSize: '14px', fontWeight: 500 }}>
                        {t('sidebar.expiresIn')} {expTimer}
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={parseInt(expirationWidth[idx])}
                            sx={{
                                height: 8,
                                borderRadius: 4,
                                backgroundColor: '#ffeaa7',
                                '& .MuiLinearProgress-bar': {
                                    background: 'linear-gradient(90deg, #f39c12 0%, #e67e22 100%)',
                                    borderRadius: 4,
                                    boxShadow: '0 2px 4px rgba(243, 156, 18, 0.3)',
                                },
                            }}
                        />
                    </TimerContainer>
                </Zoom>
            ))}
                        </>
                    )}
                </SidebarContainer>
            </Slide>
    )
}